import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InfoCard } from '@pacer/shared';
import * as api from '../api';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { Vault } from '../components/Vault';
import { Button, Card, Field, IconButton, Input, PageHeader, Spinner, Textarea } from '../components/ui';
import { queryKeys } from '../state/queries';
import { confirmDelete, toast } from '../state/ui';

/**
 * The household reference board — the "fridge binder": WiFi, contacts, house info anyone with
 * the household session can read. Below it sits the Vault, which is separately PIN-locked.
 */
export function Info() {
  const { data: cards = [], isLoading } = useQuery({ queryKey: queryKeys.info, queryFn: api.getInfoCards });
  const [editing, setEditing] = useState<InfoCard | 'new' | null>(null);

  if (isLoading) return <Spinner label="loading" />;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 md:px-8 md:py-10">
      <PageHeader
        eyebrow="Household"
        title="Info"
        actions={<Button size="sm" icon="plus" onClick={() => setEditing('new')}>Add card</Button>}
      />

      {cards.length === 0 ? (
        <button
          onClick={() => setEditing('new')}
          className="mb-8 flex w-full flex-col items-center gap-2 rounded-lg border border-dashed border-border-subtle bg-surface/30 px-6 py-10 transition-colors hover:border-border hover:bg-surface-high/40"
        >
          <Icon name="home" size={24} className="text-muted" />
          <span className="text-[14px] text-secondary">Add the first card</span>
          <span className="font-mono text-[12px] text-muted">WiFi password · doctor · emergency contacts · house rules</span>
        </button>
      ) : (
        <div className="mb-8 grid gap-3 sm:grid-cols-2">
          {cards.map((c) => (
            <Card key={c.id} className="group flex flex-col gap-1.5 p-4">
              <div className="flex items-center gap-2">
                <h3 className="flex-1 font-display text-[16px] text-primary">{c.title}</h3>
                <div className="flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  <IconButton icon="settings" label="Edit" size="sm" onClick={() => setEditing(c)} />
                </div>
              </div>
              {c.body && <p className="whitespace-pre-wrap break-words text-[13.5px] leading-relaxed text-secondary">{c.body}</p>}
            </Card>
          ))}
        </div>
      )}

      <Vault />

      {editing && (
        <InfoCardModal card={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function InfoCardModal({ card, onClose }: { card: InfoCard | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(card?.title ?? '');
  const [body, setBody] = useState(card?.body ?? '');

  const done = async () => {
    await qc.invalidateQueries({ queryKey: queryKeys.info });
    onClose();
  };

  const save = useMutation({
    mutationFn: () =>
      card ? api.updateInfoCard(card.id, { title: title.trim(), body }) : api.createInfoCard({ title: title.trim(), body }),
    onSuccess: done,
    onError: () => toast('Could not save the card.', 'error'),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteInfoCard(card!.id),
    onSuccess: done,
    onError: () => toast('Could not delete the card.', 'error'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Info"
      title={card ? 'Edit card' : 'New card'}
      footer={
        <div className="flex w-full items-center">
          {card && (
            <Button
              variant="ghost"
              size="sm"
              icon="trash"
              onClick={async () => {
                if (await confirmDelete(card.title)) remove.mutate();
              }}
            >
              Delete
            </Button>
          )}
          <Button
            className="ml-auto"
            onClick={() => title.trim() ? save.mutate() : toast('Give it a title.', 'error')}
            disabled={save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Title">
          <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="WiFi" />
        </Field>
        <Field label="Details">
          <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Network: Henderson · Password: …" />
        </Field>
      </div>
    </Modal>
  );
}
